import { useContext, useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import NitroAdSidebar from "../../ads/NitroAdSidebar";
import EnterpriseService from "../../common/services/enterprise-service";
import { useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Breadcrumbs from "../components/Breadcrumbs";
import config from "../../config";
import { getComponentsLimits } from "../../common/utils/DashboardUtils";
import { EnterpriseContext } from "../../components/enterprise/EnterpriseContext";
import ExclusivePreviewDashboardAlert from "../components/dashboardGallery/ExclusivePreviewDashboardAlert";
import DashboardTier from "../components/dashboardGallery/DashboardTier";
import BoxDynamicAd from "../../ads/BoxDynamicAd";

export default function DashboardDetail({ container }) {
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const params = useParams();
  const { uuid } = params;

  useEffect(() => {
    geData();
  }, []);

  const geData = async () => {
    setLoading(true);
    const result = await es.getDashboard(uuid);
    setDashboard(result.dashboard);
    setLoading(false);
  };

  const likeDashboard = async () => {
    if (!dashboard) return;
    await es.likeDashboard(dashboard.uuid);
    setDashboard({ ...dashboard, user_like: true, likes: dashboard.likes + 1 });
  };

  const unlikeDashboard = async () => {
    if (!dashboard) return;
    await es.dislikeDashboard(dashboard.uuid);
    setDashboard({
      ...dashboard,
      user_like: false,
      likes: dashboard.likes - 1,
    });
  };

  const downloadSourceFile = async () => {
    if (!dashboard) return;
    setLoading(true);
    // Fetch the JSON content and force download
    const response = await fetch(dashboard.contents_url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${dashboard.title
      .toLowerCase()
      .replace(/ /g, "-")}-dashboard.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setLoading(false);
  };

  return (
    <div>
      <LoadingOverlay active={loading} spinner={<Spinner color="primary" style={{ width: "4rem", height: "4rem" }} />}>
        <Row>
          <Col>
            {dashboard && (
              <div>
                <Row className="g-4">
                  {/* Main column */}
                  <Col lg="8" md="12">
                    <Card className="mb-4" style={{ position: "relative", overflow: "hidden" }}>
                      <DashboardTier dashboard={dashboard} />
                      <CardBody>
                        <Breadcrumbs breadcrumbs={[{ to: "/dashboards", title: "Dashboards Gallery" }, { title: dashboard.title }]} />
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <h1 className="mb-0">{dashboard.title}</h1>
                          <Badge color="info">
                            <FontAwesomeIcon icon="cubes" className="mx-0 me-1" />
                            {dashboard.components_number}
                          </Badge>
                        </div>
                        <div className="dashboard-detail-preview mb-3">
                          <img
                            src={dashboard.preview_image_url}
                            alt={dashboard.title}
                            className="dashboard-detail-img"
                          />
                        </div>
                        {dashboard.description && (
                          <div className="mb-3">
                            <h5>Description</h5>
                            <p className="text-muted">{dashboard.description}</p>
                          </div>
                        )}
                      </CardBody>
                    </Card>

                    {dashboard.tags && dashboard.tags.length > 0 && (
                      <Card className="mb-4">
                        <CardBody>
                          <h5 className="mb-3">
                            <FontAwesomeIcon icon="tags" className="me-2 text-primary" />
                            Tags
                          </h5>
                          <div className="d-flex flex-wrap gap-2">
                            {dashboard.tags.map((tag) => (
                              <span key={tag} className="badge bg-primary fs-6 px-3 py-2">{tag}</span>
                            ))}
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </Col>

                  {/* Sidebar */}
                  <Col lg="4" md="12">
                    {/* Tier alert */}
                    <Card className="mb-3">
                      <CardBody>
                        {dashboard.components_number > 20 && (
                          <Alert color="premium" className="text-black mb-0">
                            <FontAwesomeIcon icon="crown" className="me-2" />
                            Requires <strong>Patreon Angel</strong> tier
                          </Alert>
                        )}
                        {dashboard.components_number > 5 && dashboard.components_number <= 20 && (
                          <Alert color="premium" className="text-black mb-0">
                            <FontAwesomeIcon icon="star" className="me-2" />
                            Requires <strong>Patreon Fan</strong> tier
                          </Alert>
                        )}
                        {dashboard.components_number <= 5 && (
                          <Alert color="success" className="mb-0">
                            <FontAwesomeIcon icon="check-circle" className="me-2" />
                            <strong>Free</strong> — available for everyone
                          </Alert>
                        )}
                      </CardBody>
                    </Card>

                    {/* Details */}
                    <Card className="company-info-box mb-3">
                      <CardBody className="p-0">
                        <div className="company-info-rows">
                          <div className="company-info-row">
                            <span className="company-info-label">Author</span>
                            <div className="d-flex align-items-center gap-2">
                              <img src={dashboard.user.avatar_url} alt={dashboard.user.name} className="rounded" style={{ width: 24, height: 24, objectFit: "cover" }} />
                              <a href={`/user/${dashboard.user.id}`} className="company-info-value">{dashboard.user.name}</a>
                            </div>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">Components</span>
                            <span className="company-info-value">{dashboard.components_number}</span>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">Version</span>
                            <span className="company-info-value">{dashboard.version}</span>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">
                              <FontAwesomeIcon icon="download" className="mx-0 me-1" />Downloads
                            </span>
                            <span className="company-info-value fw-bold">{dashboard.downloads}</span>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">
                              <FontAwesomeIcon icon="thumbs-up" className="mx-0 me-1" />Likes
                            </span>
                            <span className="company-info-value fw-bold">{dashboard.likes}</span>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">Created</span>
                            <span className="company-info-value">{new Date(dashboard.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="company-info-row">
                            <span className="company-info-label">Updated</span>
                            <span className="company-info-value">{new Date(dashboard.updated_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    {/* CTAs */}
                    <Card className="mb-3">
                      <CardBody className="d-flex flex-column gap-2">
                        <Button
                          tag="a"
                          href={`${config.CLIENT_PROTOCOL_URL}://download-dashboard?uuid=${dashboard.uuid}`}
                          color="success"
                          size="lg"
                          block
                          disabled={dashboard.components_number > getComponentsLimits(dashboard, container.dataService.data.patreonMember, globalContext.member)}
                        >
                          <FontAwesomeIcon icon="download" className="me-2" />
                          Add to Dashboard Designer
                        </Button>
                        <Button
                          onClick={downloadSourceFile}
                          color="secondary"
                          size="lg"
                          block
                          disabled={dashboard.components_number > getComponentsLimits(dashboard, container.dataService.data.patreonMember, globalContext.member)}
                        >
                          <FontAwesomeIcon icon="file-code" className="me-2" />
                          Download Source File
                        </Button>
                        {!dashboard.user_like ? (
                          <Button color="primary" size="lg" block outline onClick={likeDashboard}>
                            <FontAwesomeIcon icon="thumbs-up" className="me-2" />
                            Like ({dashboard.likes})
                          </Button>
                        ) : (
                          <Button color="primary" size="lg" block onClick={unlikeDashboard}>
                            <FontAwesomeIcon icon="thumbs-up" className="me-2" />
                            Remove Like ({dashboard.likes})
                          </Button>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </Col>
          <div className="ad-column-placeholder">
            {!container.dataService.data.enablePremiumFeature() && (
              <NitroAdSidebar container={container} />
            )}
          </div>
        </Row>
      </LoadingOverlay>
    </div>
  );
}
